import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FLineIcon } from './FLineIcons';
import oolaWordmark from '../../../Assets/oola_w.svg';
import { readableFColor } from '../../../util/readableBrandColor';
import './OolaWheelMilestoneV2.css';

const SIZE = 460;
const CX = 230;
const CY = 230;
const HUB = 56;
const MAX_R = 172;
const MIN_R = HUB + 58;
const GAP = 4;
const SEG = 360 / 7 - GAP;
const GUIDE_RS = [95, 125, 155, 178];

/** Stagger + duration for radius grow (ms) */
const GROW_STAGGER = 90;
const GROW_DURATION = 900;
const LABEL_DELAY = 180;

function polar(cx, cy, r, deg) {
    const a = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function petal(cx, cy, r0, r1, a0, a1) {
    if (!r1 || r1 <= r0 + 2) return null;
    const d = ((a1 - a0) + 360) % 360;
    const large = d > 180 ? 1 : 0;
    const p0 = polar(cx, cy, r1, a0);
    const p1 = polar(cx, cy, r1, a1);
    const p2 = polar(cx, cy, r0, a1);
    const p3 = polar(cx, cy, r0, a0);
    return `M ${p0.x} ${p0.y} A ${r1} ${r1} 0 ${large} 1 ${p1.x} ${p1.y} L ${p2.x} ${p2.y} A ${r0} ${r0} 0 ${large} 0 ${p3.x} ${p3.y} Z`;
}

function sumQuestions(f) {
    return (f.questions || []).reduce(
        (pv, cv) => pv + (cv.score != null ? Number(cv.score) : 0),
        0
    );
}

function resolveTotal(f) {
    if (f.TotalScore != null && !Number.isNaN(Number(f.TotalScore))) {
        return Number(f.TotalScore);
    }
    const q = sumQuestions(f);
    if (q > 0) return q;
    // Step 2 goals: area `score` is 0–10 (display average)
    if (f.score != null && !Number.isNaN(Number(f.score)) && Number(f.score) > 0) {
        return Number(f.score) * 10;
    }
    return null;
}

/** Map TotalScore (≈0–100) → outer radius */
function scoreRadius(total, isActive, hot = false) {
    if (total == null || Number.isNaN(Number(total))) {
        return HUB + 8;
    }
    const t = Math.max(0, Math.min(100, Number(total)));
    const r = MIN_R + (t / 100) * (MAX_R - MIN_R);
    const bump = isActive ? (hot ? 14 : 6) : 0;
    return Math.min(MAX_R + bump, r + bump);
}

function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Mock-matched Oola wheel for section-complete milestone.
 * Petals grow from the hub to each F’s score on enter.
 * `celebrate` = Step 1 complete: full radii, Oola Score hub, no focus footer.
 */
function computeOolaScore(Fs) {
    let total = 0;
    for (const f of Fs || []) {
        const totalF = (f.questions || []).reduce(
            (pv, cv) => pv + (cv.score != null ? Number(cv.score) : 0),
            0
        );
        total += totalF + totalF * (Number(f.weight) || 0);
    }
    return Math.round(total);
}

export default function OolaWheelMilestoneV2({
    Fs = [],
    activeIndex = 0,
    nextName,
    nextColor,
    focusScore,
    celebrate = false,
    focusHub = false,
    /** Step 2 Goals: wheel doubles as F progress + nav (other screens omit this). */
    goalProgress = false,
    lastReachedIndex = 0,
    onSelectF,
    hideFooter = false,
    /** Thumbnail (profile strip): same petal math as celebrate, no grow / labels / hub copy. */
    compact = false,
    /** When `celebrate`, show this total instead of summing question scores. */
    hubScore = null,
}) {
    const uid = useRef(`mw${Math.random().toString(36).slice(2, 8)}`).current;
    const grewOnce = useRef(false);
    const growKeyRef = useRef('');
    const active = Fs[activeIndex] || Fs[0] || {};
    const skipGrow = compact || prefersReducedMotion();
    const [progress, setProgress] = useState(() => (skipGrow ? 1 : 0));
    const [labelsOn, setLabelsOn] = useState(() => skipGrow);
    const oolaScore = useMemo(() => {
        if (!celebrate) return null;
        if (hubScore != null && Number.isFinite(Number(hubScore))) {
            return Math.round(Number(hubScore));
        }
        return computeOolaScore(Fs);
    }, [celebrate, Fs, hubScore]);
    const [displayScore, setDisplayScore] = useState(0);

    // Identity of scores only — activeIndex must NOT restart the grow animation
    const growKey = useMemo(
        () =>
            `${celebrate ? 1 : 0}|${goalProgress ? 1 : 0}|${(Fs || [])
                .map((f) => `${f.name || ''}:${resolveTotal(f) ?? 'x'}`)
                .join(',')}`,
        [Fs, celebrate, goalProgress]
    );

    useEffect(() => {
        // Compact thumbnails hide hub copy — skip score count-up entirely.
        if (compact || !celebrate || oolaScore == null) {
            setDisplayScore(0);
            return undefined;
        }
        if (prefersReducedMotion()) {
            setDisplayScore(oolaScore);
            return undefined;
        }
        setDisplayScore(0);
        let raf = 0;
        const start = performance.now();
        const duration = 1400;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            setDisplayScore(Math.round(easeOutCubic(t) * oolaScore));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [celebrate, oolaScore, compact]);

    const baseSlices = useMemo(() => {
        const reachedCap = Math.max(0, Number(lastReachedIndex) || 0);
        return Fs.map((f, i) => {
            const total = resolveTotal(f);
            const hasScore = total != null;
            const isActive = !celebrate && !compact && i === activeIndex;
            const locked = goalProgress && i > reachedCap;
            const completed = goalProgress && i < reachedCap;
            const reachable = goalProgress && i <= reachedCap;
            // Milestone "done" = has score; goalProgress adds completed/locked
            const done = goalProgress ? completed || hasScore : hasScore;
            const targetR = scoreRadius(
                total,
                // hideFooter screens (Arrange / Oola One): highlight via CSS only —
                // do not resize petals when activeIndex changes
                celebrate || (isActive && !hideFooter),
                goalProgress && isActive
            );
            const a0 = i * (360 / 7) + GAP / 2;
            const a1 = a0 + SEG;
            const mid = (a0 + a1) / 2;
            const tick = polar(
                CX,
                CY,
                GUIDE_RS[GUIDE_RS.length - 1],
                a0 - GAP / 2
            );
            const targetScore = hasScore ? total / 10 : null;
            return {
                id: f.id || f.areaId || f.name || i,
                name: f.name,
                color: f.color || '#15c285',
                labelColor: readableFColor(f.name, f.color || '#15c285'),
                index: i,
                done,
                hasScore,
                completed,
                locked,
                reachable,
                targetScore,
                shown: hasScore ? (total / 10).toFixed(1) : null,
                isActive,
                targetR,
                a0,
                a1,
                mid,
                tick,
            };
        });
    }, [Fs, activeIndex, celebrate, compact, goalProgress, lastReachedIndex, hideFooter]);

    useEffect(() => {
        if (compact) {
            setProgress(1);
            setLabelsOn(true);
            grewOnce.current = true;
            growKeyRef.current = growKey;
            return undefined;
        }
        // Same score data: keep petals as-is; only active highlight updates via baseSlices
        if (grewOnce.current && growKeyRef.current === growKey) {
            setProgress(1);
            setLabelsOn(true);
            return undefined;
        }

        growKeyRef.current = growKey;

        if (prefersReducedMotion()) {
            setProgress(1);
            setLabelsOn(true);
            grewOnce.current = true;
            return undefined;
        }

        setProgress(0);
        setLabelsOn(false);
        grewOnce.current = false;
        let raf = 0;
        const start = performance.now();
        const n = Math.max(1, Fs?.length || 7);
        const totalTime = GROW_DURATION + GROW_STAGGER * Math.max(0, n - 1);

        const tick = (now) => {
            const elapsed = now - start;
            setProgress(Math.min(1, elapsed / totalTime));
            if (elapsed < totalTime) {
                raf = requestAnimationFrame(tick);
            } else {
                setProgress(1);
                setLabelsOn(true);
                grewOnce.current = true;
            }
        };

        raf = requestAnimationFrame(tick);
        const labelTimer = window.setTimeout(() => {
            setLabelsOn(true);
        }, Math.min(totalTime * 0.55, GROW_DURATION) + LABEL_DELAY);

        return () => {
            cancelAnimationFrame(raf);
            window.clearTimeout(labelTimer);
        };
    }, [growKey, Fs?.length, compact]);

    const slices = useMemo(() => {
        const n = Math.max(1, baseSlices.length);
        const totalTime =
            GROW_DURATION + GROW_STAGGER * Math.max(0, n - 1);

        return baseSlices.map((s) => {
            const startAt = (s.index * GROW_STAGGER) / totalTime;
            const endAt = (s.index * GROW_STAGGER + GROW_DURATION) / totalTime;
            const local =
                progress <= startAt
                    ? 0
                    : progress >= endAt
                      ? 1
                      : (progress - startAt) / (endAt - startAt);
            const t = easeOutCubic(Math.max(0, Math.min(1, local)));

            const r =
                goalProgress && s.locked
                    ? HUB + 4 + (s.targetR - HUB - 4) * t * 0.55
                    : s.done || s.isActive
                      ? HUB + 4 + (s.targetR - HUB - 4) * t
                      : HUB + 4 + (s.targetR - HUB - 4) * t * 0.35;

            const labelR = Math.min(
                r - 18,
                HUB + Math.max(0, r - HUB) * 0.58
            );
            const label = polar(
                CX,
                CY,
                Math.max(HUB + 34, labelR),
                s.mid
            );

            const liveScore =
                s.targetScore != null
                    ? (s.targetScore * t).toFixed(1)
                    : null;

            return {
                ...s,
                growT: t,
                d: petal(CX, CY, HUB, r, s.a0, s.a1),
                label,
                liveScore,
            };
        });
    }, [baseSlices, progress, goalProgress]);

    const focusLabel = active.name || 'Focus';
    const focusVal =
        focusScore ||
        (baseSlices[activeIndex] && baseSlices[activeIndex].shown) ||
        '—';
    // Fun / celebrate: hide "Current focus"
    const showFocus =
        !hideFooter && !celebrate && activeIndex >= 0 && activeIndex < 6;
    const showNext = !hideFooter && !celebrate && Boolean(nextName);
    const progressLabel = goalProgress
        ? `${focusLabel} · ${Math.min(activeIndex + 1, 7)} of 7`
        : null;

    const handleSelect = (index, locked) => {
        if (!goalProgress || !onSelectF || locked) return;
        onSelectF(index);
    };

    return (
        <div
            className={`pv2-mwheel${celebrate ? ' pv2-mwheel--celebrate' : ''}${
                goalProgress ? ' pv2-mwheel--goals' : ''
            }${compact ? ' pv2-mwheel--compact' : ''}`}
            aria-label={
                progressLabel
                    ? `Oola Wheel, ${progressLabel}`
                    : 'Oola Wheel'
            }
        >
            {progressLabel ? (
                <p className="pv2-mwheel__sr">{progressLabel}</p>
            ) : null}
            <div
                className="pv2-mwheel__stage"
                style={{
                    ['--pv2-mwheel-accent']: celebrate
                        ? '#efd078'
                        : active.color || '#15c285',
                }}
            >
                <div className="pv2-mwheel__glow" aria-hidden="true" />

                <svg className="pv2-mwheel__svg" viewBox={`0 0 ${SIZE} ${SIZE}`}>
                    <defs>
                        <filter
                            id={`${uid}-glow`}
                            x="-70%"
                            y="-70%"
                            width="240%"
                            height="240%"
                        >
                            <feGaussianBlur stdDeviation="7" result="b" />
                            <feMerge>
                                <feMergeNode in="b" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        <filter
                            id={`${uid}-glow-hot`}
                            x="-100%"
                            y="-100%"
                            width="300%"
                            height="300%"
                        >
                            <feGaussianBlur stdDeviation="5" result="b1" />
                            <feGaussianBlur stdDeviation="14" result="b2" />
                            <feMerge>
                                <feMergeNode in="b2" />
                                <feMergeNode in="b1" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        <radialGradient id={`${uid}-hub`} cx="38%" cy="30%" r="70%">
                            <stop offset="0%" stopColor="rgba(255,255,255,0.1)" />
                            <stop offset="100%" stopColor="rgba(4,6,10,0.98)" />
                        </radialGradient>
                        {slices.map((s) => (
                            <linearGradient
                                key={s.id}
                                id={`${uid}-g${s.index}`}
                                x1="18%"
                                y1="0%"
                                x2="88%"
                                y2="100%"
                            >
                                {s.isActive && goalProgress ? (
                                    <>
                                        <stop
                                            offset="0%"
                                            stopColor={s.color}
                                            stopOpacity="1"
                                        />
                                        <stop
                                            offset="45%"
                                            stopColor={s.color}
                                            stopOpacity="0.92"
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor={s.color}
                                            stopOpacity="0.68"
                                        />
                                    </>
                                ) : (
                                    <>
                                        <stop
                                            offset="0%"
                                            stopColor={s.color}
                                            stopOpacity={
                                                s.isActive
                                                    ? '0.98'
                                                    : s.completed
                                                      ? '0.82'
                                                      : s.locked
                                                        ? '0.35'
                                                        : '0.78'
                                            }
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor={s.color}
                                            stopOpacity={
                                                s.isActive
                                                    ? '0.55'
                                                    : s.completed
                                                      ? '0.32'
                                                      : s.locked
                                                        ? '0.08'
                                                        : '0.22'
                                            }
                                        />
                                    </>
                                )}
                            </linearGradient>
                        ))}
                    </defs>

                    {GUIDE_RS.map((gr) => (
                        <circle
                            key={`gr-${gr}`}
                            cx={CX}
                            cy={CY}
                            r={gr}
                            fill="none"
                            stroke="rgba(255,255,255,0.07)"
                            strokeWidth="1"
                        />
                    ))}
                    {slices.map((s) => (
                        <circle
                            key={`tk-${s.id}`}
                            cx={s.tick.x}
                            cy={s.tick.y}
                            r="2.2"
                            fill="rgba(255,255,255,0.28)"
                        />
                    ))}

                    {slices.map((s) =>
                        s.d ? (
                            <path
                                key={s.id}
                                d={s.d}
                                fill={`url(#${uid}-g${s.index})`}
                                filter={
                                    s.isActive && s.growT > 0.7
                                        ? `url(#${uid}-${
                                              goalProgress
                                                  ? 'glow-hot'
                                                  : 'glow'
                                          })`
                                        : undefined
                                }
                                stroke={
                                    s.isActive
                                        ? s.color
                                        : s.completed
                                          ? 'rgba(255,255,255,0.28)'
                                          : 'rgba(255,255,255,0.1)'
                                }
                                strokeWidth={
                                    s.isActive
                                        ? goalProgress
                                            ? 3.2
                                            : 2.8
                                        : s.completed
                                          ? 1.2
                                          : 0.9
                                }
                                className={`pv2-mwheel__fill${
                                    s.isActive ? ' is-active' : ''
                                }${s.completed ? ' is-completed' : ''}${
                                    s.locked ? ' is-locked' : ''
                                }${
                                    goalProgress && s.reachable
                                        ? ' is-clickable'
                                        : ''
                                }`}
                                opacity={
                                    s.isActive
                                        ? goalProgress
                                            ? 0.55 + 0.45 * s.growT
                                            : 0.4 + 0.6 * s.growT
                                        : s.completed
                                          ? 0.22 + 0.38 * s.growT
                                          : s.locked
                                            ? 0.08 + 0.12 * s.growT
                                            : s.done
                                              ? 0.25 + 0.67 * s.growT
                                              : 0.15 + 0.23 * s.growT
                                }
                                role={
                                    goalProgress && s.reachable
                                        ? 'button'
                                        : undefined
                                }
                                tabIndex={
                                    goalProgress && s.reachable ? 0 : undefined
                                }
                                aria-label={
                                    goalProgress
                                        ? s.locked
                                            ? `${s.name}, locked`
                                            : s.completed
                                              ? `${s.name}, completed${
                                                    s.isActive
                                                        ? ', current'
                                                        : ''
                                                }`
                                              : `${s.name}${
                                                    s.isActive
                                                        ? ', current'
                                                        : ''
                                                }`
                                        : undefined
                                }
                                aria-current={
                                    goalProgress && s.isActive
                                        ? 'step'
                                        : undefined
                                }
                                onClick={() =>
                                    handleSelect(s.index, s.locked)
                                }
                                onKeyDown={(e) => {
                                    if (
                                        (e.key === 'Enter' ||
                                            e.key === ' ') &&
                                        goalProgress &&
                                        s.reachable
                                    ) {
                                        e.preventDefault();
                                        handleSelect(s.index, s.locked);
                                    }
                                }}
                                style={
                                    goalProgress && s.reachable
                                        ? { cursor: 'pointer' }
                                        : goalProgress && s.locked
                                          ? { cursor: 'not-allowed' }
                                          : undefined
                                }
                            />
                        ) : null
                    )}

                    <circle
                        cx={CX}
                        cy={CY}
                        r={HUB - 1}
                        fill={`url(#${uid}-hub)`}
                        stroke="rgba(239,208,120,0.42)"
                        strokeWidth="1.6"
                    />
                </svg>

                {compact ? (
                    <div
                        className="pv2-mwheel__hub-brand pv2-mwheel__hub-brand--compact"
                        aria-hidden="true"
                    >
                        <img
                            src={oolaWordmark}
                            alt=""
                            className="pv2-mwheel__hub-logo"
                        />
                    </div>
                ) : celebrate && oolaScore != null ? (
                    <div
                        className="pv2-mwheel__hub-brand pv2-mwheel__hub-brand--score"
                        aria-label={`Oola Score ${oolaScore}`}
                    >
                        <span className="pv2-mwheel__hub-score-label">
                            OOLA SCORE
                        </span>
                        <span className="pv2-mwheel__hub-score-value">
                            {displayScore}
                        </span>
                    </div>
                ) : focusHub ? (
                    <div
                        className="pv2-mwheel__hub-brand pv2-mwheel__hub-brand--focus"
                        aria-label={`${focusLabel} score ${focusVal}`}
                    >
                        <span
                            className="pv2-mwheel__hub-focus-name"
                            style={{
                                color: readableFColor(
                                    active.name,
                                    active.color || '#15c285'
                                ),
                            }}
                        >
                            {String(focusLabel).toUpperCase()}
                        </span>
                        <span className="pv2-mwheel__hub-score-label">
                            SCORE
                        </span>
                        <span className="pv2-mwheel__hub-score-value pv2-mwheel__hub-score-value--sm">
                            {focusVal}
                        </span>
                    </div>
                ) : (
                    <div className="pv2-mwheel__hub-brand" aria-hidden="true">
                        <img
                            src={oolaWordmark}
                            alt=""
                            className="pv2-mwheel__hub-logo"
                        />
                        <span className="pv2-mwheel__hub-wheel">WHEEL</span>
                    </div>
                )}

                {compact ? null : (
                <div
                    className={`pv2-mwheel__overlays${
                        labelsOn ? ' is-ready' : ''
                    }${goalProgress ? ' pv2-mwheel__overlays--goals' : ''}`}
                    aria-hidden={goalProgress ? undefined : 'true'}
                >
                    {slices.map((s) => {
                        const markClass = `pv2-mwheel__mark${
                            s.isActive ? ' is-active' : ''
                        }${s.done && !goalProgress ? ' is-done' : ''}${
                            s.completed ? ' is-completed' : ''
                        }${s.locked ? ' is-locked' : ''}`;
                        const markStyle = {
                            left: `${(s.label.x / SIZE) * 100}%`,
                            top: `${(s.label.y / SIZE) * 100}%`,
                            ['--c']: s.labelColor || s.color,
                            color: s.labelColor || s.color,
                            ['--label-delay']: `${0.05 + s.index * 0.05}s`,
                        };
                        const markInner = (
                            <>
                                <span className="pv2-mwheel__ico">
                                    <FLineIcon
                                        name={s.name}
                                        index={s.index}
                                    />
                                </span>
                                <span className="pv2-mwheel__name">
                                    {s.completed && !s.isActive ? '✓ ' : ''}
                                    {s.name}
                                </span>
                                <span
                                    className={`pv2-mwheel__score${
                                        s.liveScore || s.shown
                                            ? ''
                                            : ' pv2-mwheel__score--empty'
                                    }`}
                                >
                                    {s.liveScore || s.shown || '—'}
                                </span>
                            </>
                        );

                        if (goalProgress && onSelectF) {
                            return (
                                <button
                                    key={`ov-${s.id}`}
                                    type="button"
                                    className={markClass}
                                    style={markStyle}
                                    disabled={s.locked}
                                    aria-label={`${s.name}${
                                        s.completed ? ', completed' : ''
                                    }${s.isActive ? ', current' : ''}${
                                        s.locked ? ', locked' : ''
                                    }`}
                                    aria-current={
                                        s.isActive ? 'step' : undefined
                                    }
                                    onClick={() =>
                                        handleSelect(s.index, s.locked)
                                    }
                                >
                                    {markInner}
                                </button>
                            );
                        }

                        return (
                            <div
                                key={`ov-${s.id}`}
                                className={markClass}
                                style={markStyle}
                            >
                                {markInner}
                            </div>
                        );
                    })}
                </div>
                )}
            </div>

            {showFocus || showNext ? (
                <div className="pv2-mwheel__footer">
                    {showFocus ? (
                        <div className="pv2-mwheel__cell">
                            <span className="pv2-mwheel__cell-k">
                                <i className="pv2-mwheel__card-ico pv2-mwheel__card-ico--target" />
                                Current focus
                            </span>
                            <strong
                                style={{
                                    color: readableFColor(
                                        active.name,
                                        active.color || '#15c285'
                                    ),
                                }}
                            >
                                {focusLabel} {focusVal}
                            </strong>
                        </div>
                    ) : null}
                    {showNext ? (
                        <>
                            {showFocus ? (
                                <div
                                    className="pv2-mwheel__divider"
                                    aria-hidden="true"
                                />
                            ) : null}
                            <div className="pv2-mwheel__cell">
                                <span className="pv2-mwheel__cell-k">
                                    <i className="pv2-mwheel__card-ico pv2-mwheel__card-ico--next" />
                                    Next section
                                </span>
                                <strong
                                    style={{
                                        color: readableFColor(
                                            nextName,
                                            nextColor || '#abd86d'
                                        ),
                                    }}
                                >
                                    {nextName}
                                </strong>
                            </div>
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
